import {selector, selectorFamily} from 'recoil';

import {AccountGroupsService} from '../../api/services/user-account-groups.service';
import {getUserPhoto, IWorkerSelectOption} from '../../api/strategies/searchWorkersStrategy';

import {
  accountGroupSortQueryTrigger,
  accountGroupsQueryTrigger,
  accountGroupsState,
  accountGroupUsersSelectionState,
  accountGroupUsersState,
  currentAccountGroupIdState,
  currentAccountGroupState,
  userGroupPageable,
} from './atoms';

import {isFulfilled} from '../../shared/helpers/checkAllSettled';
import {getFullName} from '../../shared/helpers/getFullName';

import {UserAccountService} from '../../api/services/user-account.service';
import {IUserAccount, TypeAccountRoles} from '../../shared/types/user-account.types';
import {authState} from '../auth/atoms';

export const accountGroupsQuery = selector({
  key: 'accountGroupsQuery',
  get: async ({get}) => {
    const {user} = get(authState);
    get(accountGroupsQueryTrigger);
    const pageable = get(userGroupPageable);
    const sort = get(accountGroupSortQueryTrigger);

    const isAdmin = user?.roles?.includes(TypeAccountRoles.ADMIN);

    const res = await AccountGroupsService.getAll({
      pageable: {
        ...pageable,
        order: [sort?.[0]?.field || 'id', sort?.[0]?.sort === 'asc' ? 'ASC' : 'DESC'],
      },
      spec: isAdmin
        ? undefined
        : {
            companyId: user?.companyId as string,
          },
    });

    return res.data;
  },
  set: ({set}, newValue) => {
    set(accountGroupsState, newValue);
  },
});

export const accountGroupByIdQuery = selector({
  key: 'accountGroupByIdQuery',
  get: async ({get}) => {
    get(accountGroupsQueryTrigger);
    const id = get(currentAccountGroupIdState);

    if (!id) return null;

    const res = await AccountGroupsService.getById(id);

    return res.data.content?.[0];
  },
  set: ({set}, newValue) => {
    set(currentAccountGroupState, newValue);
  },
});

export const accountGroupUsersQuery = selector({
  key: 'accountGroupUsersQuery',
  get: async ({get}) => {
    get(accountGroupsQueryTrigger);
    const id = get(currentAccountGroupIdState);

    if (!id) return null;

    const res = await AccountGroupsService.getGroupUsers(id);

    return res.data;
  },
  set: ({set}, newValue) => {
    set(accountGroupUsersState, newValue);
  },
});

export const accountGroupUsersSelectionQuery = selector({
  key: 'accountGroupUsersSelectionQuery',
  get: async ({get}) => {
    get(accountGroupsQueryTrigger);
    const id = get(currentAccountGroupIdState);

    if (!id) return null;

    const res = await AccountGroupsService.getGroupUsers(id);

    if (res.data.length) {
      const photoUrls = await Promise.allSettled(res.data.map((i) => getUserPhoto(i.id)));

      return photoUrls.map<IWorkerSelectOption<IUserAccount>>((item, index) => {
        const nameArr = getFullName(res.data[index]).split(' ');

        return {
          value: res.data[index],
          label:
            nameArr.length === 2
              ? `${nameArr[0]} ${nameArr[1].charAt(0)}.`
              : `${nameArr[0]} ${nameArr[1].charAt(0)}.${nameArr[2].charAt(0)}.`,
          photo: isFulfilled(item) ? item.value : null,
        };
      });
    }

    return [];
  },
  set: ({set}, newValue) => {
    set(accountGroupUsersSelectionState, newValue);
  },
});

export const usersOutOfGroupQueryFamily = selectorFamily({
  key: 'usersOutOfGroupQueryFamily',
  get:
    () =>
    async ({get}) => {
      const group = get(currentAccountGroupState);

      if (!group) return [];

      const res = await UserAccountService.findOutOfGroups(TypeAccountRoles.WORKER, [group.id]);

      if (res.data.length) {
        const photoUrls = await Promise.allSettled(res.data.map((i) => getUserPhoto(i.id)));

        return photoUrls.map((item, index) => {
          const name = getFullName(res.data[index]);

          return {
            value: res.data[index],
            label: name,
            photo: isFulfilled(item) ? item.value : null,
          };
        }) as IWorkerSelectOption<IUserAccount>[];
      }

      return [];
    },
});
